import React, { useMemo } from 'react';
import NoMachinesEmptyState from 'app/components/access-sharing/EquipmentAndFleets/NoMachinesEmptyState';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import {
  AccessSharingFlowProvider,
  useAccessSharingFlowContext,
} from 'app/hooks/access-sharing';
import { ACCESS_SHARING_FLEET_STEPS } from 'app/constants/access-sharing';
import { useNavigate } from 'react-router-dom';
import {
  CreateFleetStepController,
  FleetSelectionStepController,
} from './components';
import { ConfirmNavigateAwayDialog } from 'app/components/dialogs';
import useNavigationPrompt from 'app/hooks/useNavigationPrompt';
import {
  FleetAccessSharingStepController,
  FleetCreatedStepController,
  FleetSharingOverviewStepController,
} from 'app/components/access-sharing/flows';
import { useIsMobile } from 'app/hooks/useIsMobile';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';

export default function CreateFleetPage() {
  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const isMobile = useIsMobile();

  // TODO throw it away as soon as possible
  // Refactor layout components in the future to be more flexible
  // and avoid hacks like this.
  React.useEffect(() => {
    let classNameIndex = -1;

    if (!isMobile) {
      classNameIndex = setClassNames('layout-grey');
    }
    return () => {
      if (classNameIndex >= 0) {
        removeClassNames(classNameIndex);
        classNameIndex = -1;
      }
    };
  }, [isMobile]);

  return (
    <AccessSharingFlowProvider
      stepCount={Object.keys(ACCESS_SHARING_FLEET_STEPS).length}
      startStep={ACCESS_SHARING_FLEET_STEPS.FLEET_CREATION}
    >
      <StepRenderer />
      <UnsavedChangesWarning />
    </AccessSharingFlowProvider>
  );
}

function StepRenderer() {
  const navigate = useNavigate();
  const { stepper } = useAccessSharingFlowContext();
  const { step } = stepper;

  function handleBack() {
    navigate('/access/equipment-and-fleets');
  }

  const { equipments: rawEquipments, sharesData } = useGetEquipmentData();

  const equipments = useMemo(() => {
    return rawEquipments?.filter(equipment => {
      return (
        equipment.role === AccessSharingRole.Admin ||
        equipment.role === AccessSharingRole.Owner
      );
    });
  }, [rawEquipments]);

  if (!equipments?.length) {
    return <NoMachinesEmptyState onClose={() => handleBack()} />;
  }

  switch (step) {
    case ACCESS_SHARING_FLEET_STEPS.FLEET_SELECTION:
      return (
        <FleetSelectionStepController
          fleets={sharesData?.fleets as any[]}
          onBack={handleBack}
        />
      );
    case ACCESS_SHARING_FLEET_STEPS.FLEET_CREATION:
      return (
        <CreateFleetStepController equipment={equipments} onBack={handleBack} />
      );
    case ACCESS_SHARING_FLEET_STEPS.FLEET_CREATED:
      return <FleetCreatedStepController onBack={handleBack} />;
    case ACCESS_SHARING_FLEET_STEPS.ACCESS_SHARING:
      return <FleetAccessSharingStepController />;
    case ACCESS_SHARING_FLEET_STEPS.OVERVIEW:
      return (
        <FleetSharingOverviewStepController
          fleets={sharesData?.fleets as any[]}
          onBack={handleBack}
        />
      );
    default:
      return null;
  }
}

function UnsavedChangesWarning() {
  const { isSubmitted, isEditing, selectedUsers, selectedFleetId, stepper } =
    useAccessSharingFlowContext();
  const { step } = stepper;

  const isDirty = selectedUsers.size > 0 || !!selectedFleetId;
  const isStepWithPrompt =
    step === ACCESS_SHARING_FLEET_STEPS.FLEET_CREATED ||
    step === ACCESS_SHARING_FLEET_STEPS.OVERVIEW;

  const { showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationPrompt(
      isDirty && (isStepWithPrompt || isEditing) && !isSubmitted,
    );

  return (
    <ConfirmNavigateAwayDialog
      open={showPrompt}
      onConfirm={confirmNavigation}
      onClose={cancelNavigation}
    />
  );
}
